// web/src/console/sections/OverviewSection.tsx
import type { JSX } from "react";
import { consoleApi } from "../../lib/consoleApi";
import { useConsole } from "../ConsoleContext";
import { useAsync } from "../useAsync";
import { StatCard, Sparkline, HealthBadge } from "../charts";
import { CardTitle } from "../Term";
import { fmtCount, fmtRelative } from "../format";

export function OverviewSection(): JSX.Element {
  const { tick } = useConsole();
  const users = useAsync(() => consoleApi.fetchUsers(), [tick]);
  const events = useAsync(() => consoleApi.fetchEvents(), [tick]);
  const conversations = useAsync(() => consoleApi.fetchConversations(), [tick]);
  // HISTORICAL created/deleted from the immutable event log (#228) — never
  // shrinks when a session is deleted, unlike the CURRENT counts below.
  const lifecycle = useAsync(() => consoleApi.fetchLifecycle({ range: "24h" }), [tick]);
  // CURRENT online presence (#228 Phase 2): how many users are online right now.
  const presence = useAsync(() => consoleApi.fetchPresence(), [tick]);

  if (users.loading || events.loading) return <div className="cc-skeleton" style={{ height: 200 }} />;
  if (users.error || events.error) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={() => { users.reload(); events.reload(); }}>重试</button></div>;

  const ev = events.data?.events ?? [];
  const errors = ev.filter((e) => (e.status ?? 0) >= 500 || e.eventType === "error");
  const reqByHour = new Array(24).fill(0) as number[];
  for (const e of ev) { const h = new Date(e.createdAt).getHours(); reqByHour[h] = (reqByHour[h] ?? 0) + 1; }
  const topUsers = (users.data?.users ?? []).slice().sort((a, b) => b.messages - a.messages).slice(0, 5);

  // CURRENT snapshot (#228): live, non-deleted sessions grouped by lifecycle.
  const convs = conversations.data?.conversations ?? [];
  const activeNow = convs.filter((c) => c.state === "active").length;
  const idleNow = convs.filter((c) => c.state === "idle").length;
  const errorRate = ev.length ? ((errors.length / ev.length) * 100).toFixed(1) : "0.0";

  const lc = lifecycle.data;

  return (
    <div className="cc-grid cc-grid-2">
      {/* CURRENT strip — health first (the inverted pyramid): what is true now. */}
      <div className="cc-card">
        <CardTitle>当前（实时快照）</CardTitle>
        <HealthBadge status={errors.length > 5 ? "degraded" : "healthy"} />
        <div className="cc-grid-stats" style={{ marginTop: 12 }}>
          <StatCard label="当前在线" value={String(presence.data?.onlineUsers ?? 0)} {...(presence.data && presence.data.onlineUsers > 0 ? { tone: "ok" as const } : {})} />
          <StatCard label="活跃会话" value={String(activeNow)} />
          <StatCard label="空闲会话" value={String(idleNow)} />
          <StatCard label="错误率" value={`${errorRate}%`} {...(errors.length ? { tone: "err" as const } : {})} />
        </div>
      </div>
      {/* HISTORICAL panel — immutable counts from the event log; a delete never
          shrinks "新建" (created), only the current snapshot above changes. */}
      <div className="cc-card">
        <CardTitle>历史（近 24 小时，不可变）</CardTitle>
        {lifecycle.loading ? <div className="cc-skeleton" style={{ height: 80 }} /> : lifecycle.error ? (
          <div className="cc-empty">加载失败。 <button className="cc-back" onClick={lifecycle.reload}>重试</button></div>
        ) : (
          <div className="cc-grid-stats" style={{ marginTop: 12 }}>
            <StatCard label="新建会话" value={fmtCount(lc?.created ?? 0)} />
            <StatCard label="删除会话" value={fmtCount(lc?.deleted ?? 0)} />
            <StatCard label="请求" value={fmtCount(ev.length)} />
            <StatCard label="错误" value={String(errors.length)} {...(errors.length ? { tone: "err" as const } : {})} />
          </div>
        )}
      </div>
      <div className="cc-card">
        <CardTitle>请求（按小时）</CardTitle>
        <Sparkline values={reqByHour} height={80} />
      </div>
      <div className="cc-card">
        <CardTitle>最近错误</CardTitle>
        {errors.length === 0 ? <div className="cc-empty">无</div> : errors.slice(0, 6).map((e) => (
          <div key={e.id} className="cc-barchart-row"><span>{e.status} {e.method} {e.path}</span><span /><span>{fmtRelative(e.createdAt)}</span></div>
        ))}
      </div>
      <div className="cc-card">
        <CardTitle>活跃用户</CardTitle>
        {topUsers.map((u) => (<div key={u.userId} className="cc-barchart-row"><span>{u.username}</span><span /><span>{u.messages}</span></div>))}
      </div>
    </div>
  );
}
